import { useParams } from "react-router";
import { EditIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { StyledPlaceholder } from "@vega-core/components/Loaders/StyledPlaceholder";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { isFalsy } from "@vega-core/utils/isFalsy";

import {
  useFilteredPermissions,
  useTargetUserRolePermission,
} from "../../../common/store/rolePermission.store";
import { EditRoleModal } from "../UserProfile/modals/EditRoleModal";

export type EditRoleProps = {
  subjectId: string;
  roleId: string;
  userId?: string;
  isLP?: boolean;
  subjectName: string;
  disabled?: boolean;
};

export const EditRole = ({
  subjectId,
  roleId,
  isLP = false,
  userId: propUserId,
  subjectName,
  disabled,
}: EditRoleProps) => {
  const params = useParams<{ id: string }>();
  const userId = propUserId || params.id || "";
  const {
    value: open,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);

  const { filteredPermissions } = useFilteredPermissions(userId);

  // Pass undefined for roleContext so we get the full role with all permissions
  // (visible + hidden). EditRoleModal needs to display and edit both groups.
  const {
    isLoading,
    currentPermissions,
    selectedRole,
    handlePermissionToggle,
    handleReset,
    togglingPermission,
    subjectPermissions,
  } = useTargetUserRolePermission(
    userId,
    subjectId,
    roleId,
    filteredPermissions,
  );

  if (isLoading && !selectedRole) {
    return <StyledPlaceholder className="w-full h-8" />;
  }

  if (isFalsy(selectedRole)) {
    return null;
  }

  return (
    <>
      <ThemedButton
        dataTestId="edit-role"
        disabled={disabled}
        icon={<EditIcon />}
        onClick={() => openModal()}
        size="small"
        variant="tertiary"
      />
      <EditRoleModal
        currentPermissions={currentPermissions}
        isLP={isLP}
        isLoading={isLoading}
        onClose={closeModal}
        onPermissionToggle={handlePermissionToggle}
        onReset={() => handleReset(selectedRole)}
        open={open}
        role={selectedRole}
        subjectName={subjectName}
        subjectPermissions={subjectPermissions}
        togglingPermission={togglingPermission}
      />
    </>
  );
};
